import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { COMPANY } from "@/lib/constants";

export default function Header() {
  const { lang, setLanguage, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t("nav.services") },
    { id: "gallery", label: t("nav.gallery") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm" : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            data-testid="header-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 group"
          >
            <img
              src={COMPANY.logo_url}
              alt="Modolo Multiservices"
              className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-extrabold text-[15px] md:text-base text-neutral-900 tracking-tight">
                Modolo
              </span>
              <span className="text-[10px] md:text-[11px] text-neutral-500 uppercase tracking-[0.18em]">
                Multiservices
              </span>
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="link-underline text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Language toggle */}
            <div
              data-testid="language-toggle"
              className="flex items-center rounded-full border border-neutral-200 bg-white p-[3px] text-xs font-semibold"
            >
              <button
                data-testid="lang-es"
                onClick={() => setLanguage("es")}
                className={`px-2.5 py-1 rounded-full transition-all ${
                  lang === "es" ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                ES
              </button>
              <button
                data-testid="lang-en"
                onClick={() => setLanguage("en")}
                className={`px-2.5 py-1 rounded-full transition-all ${
                  lang === "en" ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                EN
              </button>
            </div>

            <a
              data-testid="header-call-btn"
              href={`tel:${COMPANY.phone_raw}`}
              className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-[#1F9D40] transition-colors"
            >
              <Phone size={15} strokeWidth={2.4} />
              {COMPANY.phone_display}
            </a>

            <button
              data-testid="header-cta-btn"
              onClick={() => go("contact")}
              className="hidden md:inline-flex items-center gap-2 bg-[#1F9D40] text-white hover:bg-[#177d33] transition-colors text-sm font-bold px-5 py-2.5 rounded-full tracking-tight"
            >
              {t("nav.cta")}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2 -mr-2 text-neutral-900"
              aria-label="menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-neutral-200">
          <div className="px-4 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left py-3 px-2 text-base font-medium text-neutral-800 border-b border-neutral-100"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${COMPANY.phone_raw}`}
              data-testid="mobile-call-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 border border-neutral-900 text-neutral-900 py-3 rounded-full text-sm font-bold"
            >
              <Phone size={16} /> {COMPANY.phone_display}
            </a>
            <button
              data-testid="mobile-cta-btn"
              onClick={() => go("contact")}
              className="mt-2 inline-flex items-center justify-center bg-[#1F9D40] text-white py-3 rounded-full text-sm font-bold"
            >
              {t("nav.cta")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
